import DashboardLayout from '../../../../commons/DashboardLayout/DashboardLayout';
import { Bar, Line } from 'react-chartjs-2';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { usePlansENT } from '../../../../../hooks/fetchers/usePlansENT/usePlansENT';
import { useAllStatsDIR } from '../../../../../hooks/fetchers/useAllStatsDIR/useAllStatsDIR';
import Select from 'react-select';
import { useCommerciaux } from '../../../../../hooks/fetchers/useCommerciaux/useCommerciaux';
import { useMergedStatesFromFetchers } from '../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Loading from '../../../../commons/Loading/Loading';
import { useMutation, useQueryClient } from 'react-query';
import { AllStatsSpecifiedCOMQueryParams } from './DashboardStatsDIR.types';
import { AllStatsDIRResponse } from '../../../../../types/api/endpoints/stats/stats.types';
import {
  ALL_STATS_SPECIFIED_COM_QUERY_KEY,
  allStatsSpecifiedCOMQuery,
} from './DashboardStatsDIR.query';

export default () => {
  const queryClient = useQueryClient();

  const [currentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const { data: allStats, isLoading: isAllStatsLoading } = useAllStatsDIR();

  const { data: plansENT, isLoading: isPlansENTLoading } = usePlansENT();

  const { data: commerciaux, isLoading: isCommerciauxLoading } = useCommerciaux({
    page: currentPage,
    perPage: itemsPerPage,
  });

  const { mutate, isLoading: isSpecifiedStatsLoading } = useMutation<
    Awaited<AllStatsDIRResponse>,
    Error,
    AllStatsSpecifiedCOMQueryParams
  >((data) => allStatsSpecifiedCOMQuery(data), {
    mutationKey: ALL_STATS_SPECIFIED_COM_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: (response) => {
      setStatsToDisplay(response);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const [selectedCOM, setSelectedCOM] = useState('all');
  const [statsToDisplay, setStatsToDisplay] = useState<AllStatsDIRResponse>();

  useEffect(() => {
    if (selectedCOM !== 'all') {
      mutate({ commercialId: parseInt(selectedCOM) });
    }
  }, [selectedCOM]);

  useEffect(() => {
    if (selectedCOM === 'all') {
      setStatsToDisplay(allStats);
    }
  }, [allStats, selectedCOM]);

  const options = useMemo(() => {
    const commerciauxOptions =
      commerciaux?.results.map((commercial) => ({
        value: commercial.id.toString(),
        label: `${commercial.firstName} ${commercial.lastName}`,
      })) || [];
    return [{ value: 'all', label: 'Tous les commerciaux' }, ...commerciauxOptions];
  }, [commerciaux]);

  const wonStats = useMemo(
    () =>
      [...Array(6)]
        .map((_, index) => {
          // statsToDisplay?.won.reduce((acc, { count }) => [...acc, count], [] as number[])
          const [month, year] = moment().subtract(index, 'month').format('M,Y').split(',');
          return (
            statsToDisplay?.won.find(
              (item) => item.month === parseInt(month) && item.year === parseInt(year)
            )?.count || 0
          );
        })
        .reverse(),
    [statsToDisplay]
  );

  const lostStats = useMemo(
    () =>
      [...Array(6)]
        .map((_, index) => {
          // statsToDisplay?.won.reduce((acc, { count }) => [...acc, count], [] as number[])
          const [month, year] = moment().subtract(index, 'month').format('M,Y').split(',');
          return (
            statsToDisplay?.lost.find(
              (item) => item.month === parseInt(month) && item.year === parseInt(year)
            )?.count || 0
          );
        })
        .reverse(),
    [statsToDisplay]
  );

  const totalStats = useMemo(
    () =>
      [...Array(6)]
        .map((_, index) => {
          // statsToDisplay?.total.reduce((acc, { count }) => [...acc, count], [] as number[])
          const [month, year] = moment().subtract(index, 'month').format('M,Y').split(',');
          return (
            statsToDisplay?.total.find(
              (item) => item.month === parseInt(month) && item.year === parseInt(year)
            )?.count || 0
          );
        })
        .reverse(),
    [statsToDisplay]
  );

  const ratioStats = useMemo(
    () =>
      [...Array(6)]
        .map((_, index) => {
          // statsToDisplay?.win_ratio.reduce((acc, { ratio }) => [...acc, ratio], [] as number[])
          const [month, year] = moment().subtract(index, 'month').format('M,Y').split(',');
          const ratio =
            statsToDisplay?.win_ratio.find(
              (item) => item.month === parseInt(month) && item.year === parseInt(year)
            )?.ratio || 0;
          return ratio > 0 ? ratio * 100 : 0;
        })
        .reverse(),
    [statsToDisplay]
  );

  const averageStats = useMemo(
    () =>
      [...Array(6)]
        .map((_, index) => {
          // statsToDisplay?.win_ratio.reduce((acc, { ratio }) => [...acc, ratio], [] as number[])
          const [month, year] = moment().subtract(index, 'month').format('M,Y').split(',');
          const average =
            statsToDisplay?.win_average.find(
              (item) => item.month === parseInt(month) && item.year === parseInt(year)
            )?.average || 0;
          return average;
        })
        .reverse(),
    [statsToDisplay]
  );

  const planStats = useMemo(
    () =>
      plansENT?.map((currentPlan) => {
        return statsToDisplay?.plan.find((item) => item.plan === currentPlan.plan_name)?.count;
      }),
    [statsToDisplay, plansENT]
  );

  const datasets = [
    {
      label: 'Gagné',
      data: wonStats,
      borderColor: '#DE5835',
      backgroundColor: '#DE583550',
    },
    {
      label: 'Perdu',
      data: lostStats,
      borderColor: 'rgb(153, 62, 235)',
      backgroundColor: 'rgba(153, 62, 235, 0.5)',
    },
    {
      label: 'Démarché',
      data: totalStats,
      borderColor: '#206C87',
      backgroundColor: '#206C8750',
    },
  ];

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isAllStatsLoading, isPlansENTLoading, isCommerciauxLoading, isSpecifiedStatsLoading],
  });

  return (
    <DashboardLayout>
      <Select
        defaultValue={{ value: 'all', label: 'Tous les commerciaux' }}
        options={options}
        onChange={(newValue) => {
          if (newValue) {
            setSelectedCOM(newValue.value);
          }
        }}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={{
          control: (provided, state) => ({
            ...provided,
            background: '#fff',
            borderWidth: 1,
            borderColor: state.isFocused ? 'var(--primary-50) !important' : undefined,
            // boxShadow: state.isFocused ? "0px 0px 6px #DE5835" : "none",
            minHeight: '30px',
            height: '34px',
            boxShadow: 'none',
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'var(--primary-light) !important' : undefined,
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: '34px',
            padding: '0 6px',
          }),

          input: (provided) => ({
            ...provided,
            margin: '0px',
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: '34px',
          }),
        }}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <section className="dashboard__stats" style={{ marginTop: 'var(--spacing-sToM)' }}>
          <div className="dashboard__stats-box">
            <Line
              datasetIdKey="id"
              data={{
                labels: [...Array(6)]
                  .map((_, index) => moment().subtract(index, 'months').format('MMMM'))
                  .reverse(),
                datasets,
              }}
            />
          </div>
          <div className="dashboard__stats-box">
            <Bar
              datasetIdKey="id"
              data={{
                labels: plansENT?.map((plan) => plan.plan_name),
                datasets: [
                  {
                    label: 'Plan',
                    data: planStats,
                    borderColor: '#DE5835',
                    backgroundColor: '#DE583575',
                  },
                ],
              }}
            />
          </div>
          <div className="dashboard__stats-box">
            <Line
              datasetIdKey="id"
              options={{
                scales: {
                  y: {
                    min: 0,
                    max: 100,
                    ticks: {
                      callback: (value) => value + '%',
                    },
                  },
                },
              }}
              data={{
                labels: [...Array(6)]
                  .map((_, index) => moment().subtract(index, 'months').format('MMMM'))
                  .reverse(),
                datasets: [
                  {
                    label: 'Ratio de gains',
                    data: ratioStats,
                    borderColor: '#DE5835',
                    backgroundColor: '#DE583575',
                  },
                ],
              }}
            />
          </div>
          <div className="dashboard__stats-box">
            <Line
              datasetIdKey="id"
              data={{
                labels: [...Array(6)]
                  .map((_, index) => moment().subtract(index, 'months').format('MMMM'))
                  .reverse(),
                datasets: [
                  {
                    label: 'Moyenne de ventes par jour',
                    data: averageStats,
                    borderColor: '#DE5835',
                    backgroundColor: '#DE583575',
                  },
                ],
              }}
            />
          </div>
        </section>
      )}
    </DashboardLayout>
  );
};
